import React, { memo, forwardRef } from 'react';
import { useLocale } from '@react-aria/i18n';
import { useCalendarState, useRangeCalendarState } from '@react-stately/calendar';
import { createCalendar } from '@internationalized/date';
import { useCalendar, useRangeCalendar } from '@react-aria/calendar';
import { useForwardedRef } from '../../hooks';
import { CalendarBase } from './CalendarBase';
import { date } from '../../utils';
import { DEFAULT_MIN_DATE } from './consts';
export const Calendar = memo(forwardRef(function Calendar(props, ref) {
    const calendarRef = useForwardedRef(ref);
    const { locale } = useLocale();
    const finalProps = {
        ...props,
        value: date.stringToDate(props.value),
        defaultValue: date.stringToDate(props.defaultValue),
        minValue: date.stringToDate(props.minValue) || DEFAULT_MIN_DATE,
        maxValue: date.stringToDate(props.maxValue),
        onChange: (value) => {
            var _a;
            (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, date.dateToString(value, state.timeZone));
        },
    };
    const state = useCalendarState({
        ...finalProps,
        locale,
        createCalendar,
    });
    const calendarProps = useCalendar(finalProps, state);
    return (React.createElement(CalendarBase, { ref: calendarRef, ...{ ...calendarProps, locale, state, ...finalProps } }));
}));
export const RangeCalendar = memo(forwardRef(function RangeCalendar(props, ref) {
    const calendarRef = useForwardedRef(ref);
    const { locale } = useLocale();
    let state;
    const minValue = date.stringToDate(props.minValue) || DEFAULT_MIN_DATE;
    const maxValue = date.stringToDate(props.maxValue);
    const finalProps = {
        ...props,
        value: date.stringToDate(props.value),
        defaultValue: date.stringToDate(props.defaultValue),
        minValue,
        maxValue,
        singleMonth: props.singleMonth,
        onChange: (value) => {
            var _a;
            if ((minValue && value.start.compare(minValue) < 0) ||
                (maxValue && value.end.compare(maxValue) > 0)) {
                return;
            }
            (_a = props.onChange) === null || _a === void 0 ? void 0 : _a.call(props, date.dateRangeToString(value, state.timeZone));
        },
    };
    const isDateUnavailable = date => {
        if (!state) {
            return false;
        }
        const { value, isDragging, anchorDate, highlightedRange } = state;
        const isStartDraggingOnExistentHighlightedStart = isDragging &&
            anchorDate &&
            highlightedRange.start &&
            (value === null || value === void 0 ? void 0 : value.end) &&
            (value === null || value === void 0 ? void 0 : value.start) &&
            highlightedRange.start.compare(value.start) === 0;
        const minDays = props.minDays;
        const maxDays = props.maxDays;
        if (isStartDraggingOnExistentHighlightedStart || !anchorDate || (!minDays && !maxDays)) {
            return false;
        }
        return ((!!minDays && Math.abs(date.compare(anchorDate)) < minDays) ||
            (!!maxDays && Math.abs(date.compare(anchorDate)) > maxDays));
    };
    state = useRangeCalendarState({
        ...finalProps,
        isDateUnavailable,
        locale,
        createCalendar,
        visibleDuration: { months: props.singleMonth ? 1 : 2 },
    });
    const calendarProps = useRangeCalendar(finalProps, state, calendarRef);
    return (React.createElement(CalendarBase, { ref: calendarRef, ...{ ...calendarProps, locale, state, ...finalProps } }));
}));
